import { DisplayMarket, SKELETON_DISPLAY_MARKET } from "@silo/types";
import { cn } from "../../utils/class-names";
import { match, P } from "ts-pattern";
import { Warning } from "../warning";
import { TokenContext } from "../token-context";
import { ReactNode } from "react";
import { useBreakpointValue } from "../../hooks/use-breakpoint-value";
import { LogoSize, logoSizeMap } from "../logo";
import { DeprecatedChip, UnstableOracleChip } from "../alert-chip";
import { AvailableToBorrow } from "./available-to-borrow";
import { SkeletonWrapper } from "../skeleton";
import { LiquidityCell, MarketCell, MarketYieldsCell, BaseRow } from "./shared";
import { AssetYields } from "./asset-yields";
import { Option, pipe } from "effect";
import { getProtocolConfig } from "@silo/config";
import { isNotNullable } from "effect/Predicate";
import { tv } from "tailwind-variants";

export const row = tv({
  base: "first:rounded-t-lg bg-shale-800 hover:bg-shale-750 duration-150 hover:ease-in cursor-pointer",
});

export const useMarketLogoSize = () => {
  const logoSize = useBreakpointValue<LogoSize>("md", { lg: "lg" });

  return logoSize;
};

export const tokenLogoSize: LogoSize = "sm";

export interface RowContentProps {
  className?: string;
  market: DisplayMarket;
  marketLogo: ReactNode;
  tokenLogo: ReactNode;
}

export function RowContent({
  className,
  market,
  marketLogo,
  tokenLogo,
}: RowContentProps): JSX.Element {
  const {
    warningConfig,
    chainKey,
    marketTokenAddress,
    marketSymbol,
    marketName,
    hasUnstableOracle,
    isDeprecated,
    protocolKey,
    baseAsset,
    bridgeAssets: [firstBridgeAsset, secondBridgeAsset],
  } = market;

  const nativeRewardsSymbol = pipe(
    getProtocolConfig(protocolKey),
    (config) => config.nativeRewards,
    Option.fromNullable,
    Option.map((nativeRewards) => nativeRewards.symbol),
    Option.getOrElse(() => "")
  );

  const hasChip = hasUnstableOracle || isDeprecated;

  return (
    <BaseRow className={className} data-cy="market-line-display">
      <MarketCell>
        {match(warningConfig)
          .with({ scope: P.union("all", "markets") }, ({ warningKey }) => (
            <Warning.TooltipWarning warningKey={warningKey} />
          ))
          .otherwise(() => null)}

        <TokenContext.Provider
          value={{ chainKey, tokenAddress: marketTokenAddress }}
        >
          {marketLogo}
        </TokenContext.Provider>

        <div className="flex flex-col">
          <div className="font-bold">{marketSymbol}</div>
          <div className="hidden text-xs text-shale-200 lg:block">
            {marketName}
          </div>
        </div>

        {hasChip && (
          <div className="flex items-center gap-1">
            {hasUnstableOracle && <UnstableOracleChip iconOnly />}
            {isDeprecated && <DeprecatedChip iconOnly />}
          </div>
        )}
      </MarketCell>

      <LiquidityCell>
        <AvailableToBorrow market={market} tokenLogo={tokenLogo} />
      </LiquidityCell>

      <MarketYieldsCell protocolKey={protocolKey}>
        <AssetYields
          asset={baseAsset}
          nativeRewardsSymbol={nativeRewardsSymbol}
          dataCy="base-apy"
        />

        <AssetYields
          asset={firstBridgeAsset}
          nativeRewardsSymbol={nativeRewardsSymbol}
          dataCy="first-bridge-apy"
        />

        {isNotNullable(secondBridgeAsset) && (
          <AssetYields
            asset={secondBridgeAsset}
            nativeRewardsSymbol={nativeRewardsSymbol}
            dataCy="second-bridge-apy"
          />
        )}
      </MarketYieldsCell>
    </BaseRow>
  );
}

interface PulseRowProps {
  className?: string;
}

export function PulseRow({ className }: PulseRowProps): JSX.Element {
  const marketLogoSize = useMarketLogoSize();

  return (
    <SkeletonWrapper className={cn("rounded-none", className)}>
      <RowContent
        market={SKELETON_DISPLAY_MARKET}
        marketLogo={
          <div
            style={{
              width: logoSizeMap[marketLogoSize],
              height: logoSizeMap[marketLogoSize],
            }}
          ></div>
        }
        tokenLogo={
          <div
            style={{
              width: logoSizeMap[tokenLogoSize],
              height: logoSizeMap[tokenLogoSize],
            }}
          ></div>
        }
      />
    </SkeletonWrapper>
  );
}
